import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, CalendarClock, Calculator, FileUp, Boxes, LogOut, Bot, Image, Users, Contact, ShoppingCart, Settings, Tag, BarChart3, FileImage, Sparkles, Megaphone, History, AlertTriangle, Truck, PackageSearch, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Analytics from "@/pages/admin/Analytics";
import Volantini from "@/pages/admin/Volantini";
import Copilot from "@/pages/admin/Copilot";
import Promozioni from "@/pages/admin/Promozioni";
import PriceHistory from "@/pages/admin/PriceHistory";
import PricingRules from "@/pages/admin/PricingRules";
import Suppliers from "@/pages/admin/Suppliers";
import NWGLists from "@/pages/admin/NWGLists";
import Valutazioni from "@/pages/admin/Valutazioni";
import UsatoAdmin from "@/pages/admin/Usato";
import Listini from "@/pages/admin/Listini";
import { fetchDashboard, staffRepairs, patchRepair, pricingSimulate,
  inventoryList, inventoryMovement, euro, REPAIR_STATUS_META,
  aiGetConfig, aiPutConfig, aiUsage, aiConversations,
  listPriceLists, listImports, createImport, getImport, approveImport, publishImport,
  listUsers, createUser, deleteUser, adminCatalog, fetchCatalogImages, productImages, searchImages, addImage, approveImage, rejectImage,
  crmMeta, listLeads, patchLead, adminOrders, patchOrder, bankConfig, putBankConfig, waConfig,
  shipmentUpdate, shippingConfig, putShippingConfig, ppConfig, putPpConfig, SHIPMENT_STATUSES, SHIPMENT_META,
  ORDER_FLOW, ORDER_STATUS_META, waLink, expiringPromos } from "@/lib/store";

// FASE 1 — navigazione consolidata: 9 sezioni principali con sotto-tab interne.
// Nessun componente eliminato: le voci non elencate (immagini, magazzino, ordini, crm, catalogo)
// restano nel codice e nel render switch, ma fuori dalla navigazione principale.
const SECTIONS = [
  { id: "dash", label: "Dashboard", icon: LayoutDashboard, subs: [["dash", "Dashboard"]] },
  { id: "agenda", label: "Agenda", icon: CalendarClock, subs: [["agenda", "Agenda"]] },
  { id: "valutazioni", label: "Valutazioni Usato", icon: Smartphone, subs: [["valutazioni", "Valutazioni Usato"]] },
  { id: "usato", label: "Usato", icon: PackageSearch, subs: [["usato", "Catalogo Usato"]] },
  { id: "pricing", label: "Pricing", icon: Calculator, subs: [["pricing", "Simulatore & Margini"], ["storico", "Storico Prezzi"]] },
  { id: "analytics", label: "Analytics", icon: BarChart3, subs: [["analytics", "Analytics"]] },
  { id: "fornitori", label: "Fornitori", icon: Truck, subs: [["fornitori", "Fornitori"], ["import", "Import Listini"], ["nwg", "Listini NWG"], ["listini-man", "Listini Manuali"]] },
  { id: "ai", label: "AI", icon: Bot, subs: [["ai", "Assistente AI"], ["copilot", "Copilot"]] },
  { id: "social", label: "Social & Volantini", icon: Megaphone, subs: [["volantini", "Volantini"], ["social", "Carosello & Promo"]] },
  { id: "settings", label: "Impostazioni", icon: Settings, subs: [["settings", "Generali"], ["utenti", "Utenti"]] },
];

const KPI = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-[#11141A] p-4"><div className="text-2xl font-bold text-white">{value}</div><div className="mt-1 text-xs text-zinc-500">{label}</div></div>
);

function Dashboard() {
  const [d, setD] = useState(null);
  const [expiring, setExpiring] = useState([]);
  useEffect(() => { fetchDashboard().then(setD).catch(() => {}); expiringPromos(48).then(setExpiring).catch(() => {}); }, []);
  if (!d) return <p className="text-zinc-500">Caricamento…</p>;
  return (
    <div className="space-y-6">
      {expiring.length > 0 && (
        <div data-testid="promo-expiring-alert" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="mb-2 flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-semibold">{expiring.length} promo in scadenza nelle prossime 48h</span>
          </div>
          <ul className="space-y-1">
            {expiring.map((p, i) => (
              <li key={i} data-testid={`promo-expiring-${i}`} className="flex flex-wrap items-center gap-x-2 text-sm text-amber-100/90">
                <span className="font-medium text-white">{p.product_name || "Prodotto"}</span>
                {p.variant_label && <span className="text-xs text-amber-200/70">({p.variant_label})</span>}
                <span className="text-emerald-400">{euro(p.promo_price_cents)}</span>
                <span className="text-amber-200/70">· scade il {p.promo_end ? new Date(p.promo_end).toLocaleString("it-IT") : "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <section><h3 className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Riparazioni</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{Object.entries(d.riparazioni).map(([k, v]) => <KPI key={k} label={REPAIR_STATUS_META[k]?.label || k} value={v} />)}</div></section>
      <section><h3 className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Magazzino</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{Object.entries(d.magazzino).map(([k, v]) => <KPI key={k} label={k} value={v} />)}</div></section>
      <section><h3 className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Vendite / Lead / Import</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KPI label="Fatturato" value={euro(d.vendite.fatturato_cents)} /><KPI label="Ordini" value={d.vendite.ordini} />
          <KPI label="Lead totali" value={d.lead.totali} /><KPI label="Righe da approvare" value={d.import.da_approvare} /></div></section>
    </div>
  );
}

function Agenda() {
  const [rows, setRows] = useState([]);
  const load = () => staffRepairs({}).then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const change = async (id, status) => { await patchRepair(id, { status }); toast.success("Stato aggiornato"); load(); };
  return (
    <div className="space-y-3">
      {rows.length === 0 && <p className="text-zinc-500">Nessuna richiesta.</p>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`agenda-row-${r.request_number}`} className="rounded-xl border border-white/10 bg-[#11141A] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-[#2563EB]">#{r.request_number}</span>
            <span className="font-semibold text-white">{r.customer_name}</span>
            <span className="text-sm text-zinc-400">· {r.brand} {r.model} · {r.repair_type}</span>
            <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${REPAIR_STATUS_META[r.status]?.cls}`}>{REPAIR_STATUS_META[r.status]?.label || r.status}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
            <span>📅 {r.desired_date} {r.desired_time}</span><span>📞 {r.phone}</span>
            {r.part && <span>🔧 {r.part.model} · {r.part.quality || "—"} · {r.part.availability} · {r.part.lead_time}</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["DA_VERIFICARE", "IN_ATTESA_RICAMBIO", "CONFERMATA", "COMPLETATA", "ANNULLATA"].map((s) =>
              <button key={s} onClick={() => change(r.id, s)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300 hover:border-[#2563EB]">{REPAIR_STATUS_META[s].label}</button>)}
            <a href={`https://wa.me/${(r.whatsapp || r.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#25D366]/90 px-3 py-1 text-xs text-white">WhatsApp</a>
          </div>
        </div>
      ))}
    </div>
  );
}

function Pricing() {
  const [cost, setCost] = useState("100");
  const [comp, setComp] = useState("");
  const [res, setRes] = useState(null);
  const run = async () => {
    const competitors = comp.split(",").map((x) => Math.round(parseFloat(x.trim()) * 100)).filter((x) => !isNaN(x));
    setRes(await pricingSimulate({ cost_cents: Math.round(parseFloat(cost) * 100), competitors: competitors.length ? competitors : null }));
  };
  return (
    <div className="space-y-8">
      <PricingRules />
      <div className="max-w-xl space-y-4 border-t border-white/10 pt-6">
      <p className="text-sm text-zinc-400">Simulatore prezzi (deterministico) — prova come cambia il prezzo finale con i ricarichi impostati sopra.</p>
      <div className="flex gap-3">
        <label className="flex-1 text-sm text-zinc-400">Costo €<input className="mt-1 w-full rounded-lg border border-white/10 bg-[#11141A] px-3 py-2 text-white" value={cost} onChange={(e) => setCost(e.target.value)} /></label>
        <label className="flex-1 text-sm text-zinc-400">Concorrenti € (virgola)<input className="mt-1 w-full rounded-lg border border-white/10 bg-[#11141A] px-3 py-2 text-white" value={comp} onChange={(e) => setComp(e.target.value)} placeholder="90, 95" /></label>
      </div>
      <button data-testid="pricing-simulate-btn" onClick={run} className="rounded-full bg-[#2563EB] px-5 py-2.5 font-semibold text-white">Calcola</button>
      {res && <div className="rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
        <div className="grid grid-cols-2 gap-y-1">
          <span className="text-zinc-500">Regola</span><span className="text-right text-white">{res.rule_name}</span>
          <span className="text-zinc-500">Costo</span><span className="text-right text-white">{euro(res.cost_cents)}</span>
          <span className="text-zinc-500">Base</span><span className="text-right text-white">{euro(res.base_cents)}</span>
          <span className="text-zinc-500">Mediana concorrenti</span><span className="text-right text-white">{res.competitor_median_cents ? euro(res.competitor_median_cents) : "—"}</span>
          <span className="text-zinc-500">Target (×{res.competitor_factor})</span><span className="text-right text-white">{res.target_cents ? euro(res.target_cents) : "—"}</span>
          <span className="text-zinc-500">Arrotondamento</span><span className="text-right text-white">{res.rounding}</span>
          <span className="text-zinc-500 font-semibold">Prezzo finale</span><span className="text-right text-lg font-bold text-emerald-400">{euro(res.final_cents)}</span>
          <span className="text-zinc-500">Margine / Ricarico</span><span className="text-right text-white">{res.margin_pct}% / {res.markup_pct}%</span>
        </div>
      </div>}
    </div>
    </div>
  );
}

function ImportPanel() {
  const [lists, setLists] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const load = () => { listPriceLists().then(setLists).catch(() => {}); listImports().then(setJobs).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const runImport = async (pl) => {
    setBusy(true);
    try { const j = await createImport({ price_list_id: pl.id, list_type: pl.list_type }); toast.success(`Import ${pl.list_type}: ${j.stats.rows} righe`); load(); }
    catch (e) { toast.error("Import fallito", { description: e.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };
  const openReview = async (jid) => setReview(await getImport(jid));
  const approveAll = async (jid) => { await approveImport(jid, { all: true }); toast.success("Righe approvate"); setReview(await getImport(jid)); load(); };
  const publish = async (jid) => { const r = await publishImport(jid); r.ok ? toast.success(r.note) : toast.message(r.note); load(); setReview(null); };
  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-400">I 3 listini fornitore caricati. Importa → rivedi → approva → pubblica (i RICAMBI popolano costi/repair_parts). Nessun aumento stock automatico.</p>
      <div className="space-y-2">
        {lists.map((pl) => (
          <div key={pl.id} data-testid="price-list-row" className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
            <FileUp className="h-4 w-4 text-[#2563EB]" /><span className="font-medium text-white">{pl.name}</span>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-zinc-400">{pl.list_type}</span>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">{pl.status}</span>
            <button disabled={busy} data-testid="import-btn" onClick={() => runImport(pl)} className="ml-auto rounded-full bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Importa</button>
          </div>
        ))}
      </div>
      <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Import eseguiti</h3>
      {jobs.map((j) => (
        <div key={j.id} data-testid="import-job-row" className="rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-white">{j.filename}</span>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-zinc-400">{j.list_type}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${j.published ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{j.published ? "PUBBLICATO" : j.status}</span>
            <div className="ml-auto flex gap-2">
              <button data-testid="review-btn" onClick={() => openReview(j.id)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300">Rivedi</button>
              <button onClick={() => approveAll(j.id)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300">Approva tutto</button>
              <button data-testid="publish-btn" onClick={() => publish(j.id)} className="rounded-full bg-emerald-500/90 px-3 py-1 text-xs font-semibold text-white">Pubblica</button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-zinc-500">{Object.entries(j.stats || {}).map(([k, v]) => <span key={k}>{k}: {v}</span>)}</div>
        </div>
      ))}
      {review && (
        <div className="rounded-xl border border-[#2563EB]/40 bg-[#0B0E13] p-4">
          <div className="mb-2 flex items-center justify-between"><h4 className="font-semibold text-white">Revisione righe ({review.rows.length})</h4><button onClick={() => setReview(null)} className="text-xs text-zinc-400">Chiudi</button></div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs"><thead className="text-zinc-500"><tr><th className="p-1 text-left">Modello</th><th className="p-1 text-left">Tipo</th><th className="p-1 text-left">Qualità</th><th className="p-1 text-right">Costo</th><th className="p-1 text-left">Classe</th><th className="p-1">Appr.</th></tr></thead>
              <tbody>{review.rows.slice(0, 100).map((r) => <tr key={r.id} className="border-t border-white/5 text-zinc-300"><td className="p-1">{r.model || "—"}</td><td className="p-1">{r.part_type || "—"}</td><td className="p-1">{r.quality || "—"}</td><td className="p-1 text-right">{r.new_price_cents ? euro(r.new_price_cents) : "—"}</td><td className="p-1">{r.classification}</td><td className="p-1 text-center">{r.approved ? "✓" : "—"}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Immagini() {
  const [cat, setCat] = useState([]); const [sel, setSel] = useState(null); const [imgs, setImgs] = useState([]); const [busy, setBusy] = useState(false);
  const load = () => adminCatalog().then(setCat).catch(() => {});
  useEffect(() => { load(); }, []);
  const open = async (p) => { setSel(p); setImgs(await productImages(p.id)); };
  const doSearch = async () => { setBusy(true); try { const r = await searchImages(sel.id); r.found ? toast.success(`${r.found} immagini trovate`) : toast.message(r.note || "Nessuna immagine trovata"); setImgs(await productImages(sel.id)); } finally { setBusy(false); } };
  const addUrl = async () => { const u = window.prompt("URL immagine ufficiale (.jpg/.png/.webp):"); if (u) { await addImage(sel.id, u); setImgs(await productImages(sel.id)); toast.success("Immagine aggiunta (da approvare)"); } };
  const approve = async (i) => { await approveImage(i); setImgs(await productImages(sel.id)); load(); toast.success("Approvata"); };
  const reject = async (i) => { await rejectImage(i); setImgs(await productImages(sel.id)); load(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-2">
        <p className="text-sm text-zinc-400">Fase 7 — ricerca immagini ufficiali (EAN&gt;SKU&gt;Modello) con approvazione manuale.</p>
        {cat.map((p) => (
          <button key={p.id} data-testid="catalog-product-row" onClick={() => open(p)} className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm ${sel?.id === p.id ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/10 bg-[#11141A]"}`}>
            {p.image ? <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded bg-white/5 text-zinc-600"><Image className="h-4 w-4" /></div>}
            <span className="flex-1 text-white">{p.name}</span>
            {p.images_pending > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">{p.images_pending} da approvare</span>}
            <span className="text-xs text-zinc-500">{p.images_approved} ok</span>
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-white/10 bg-[#11141A] p-4">
        {!sel ? <p className="text-zinc-500">Seleziona un prodotto.</p> : (
          <>
            <div className="mb-3 flex items-center justify-between"><h4 className="font-semibold text-white">{sel.name}</h4>
              <div className="flex gap-2"><button disabled={busy} data-testid="img-search-btn" onClick={doSearch} className="rounded-full bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{busy ? "Cerco…" : "Cerca immagini"}</button><button onClick={addUrl} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300">+ URL</button></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {imgs.length === 0 && <p className="text-sm text-zinc-500">Nessuna immagine.</p>}
              {imgs.map((im) => (
                <div key={im.id} data-testid="product-image-card" className="overflow-hidden rounded-lg border border-white/10">
                  <img src={im.url} alt="" className="aspect-square w-full object-cover" />
                  <div className="flex items-center justify-between p-2 text-xs">
                    <span className={im.approved ? "text-emerald-400" : "text-amber-400"}>{im.approved ? "Approvata" : `${im.source} · ${Math.round((im.confidence || 0) * 100)}%`}</span>
                    <div className="flex gap-1">{!im.approved && <button data-testid="img-approve-btn" onClick={() => approve(im.id)} className="rounded bg-emerald-500/90 px-2 py-0.5 text-white">✓</button>}<button onClick={() => reject(im.id)} className="rounded bg-rose-500/90 px-2 py-0.5 text-white">✕</button></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Utenti() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "staff" });
  const load = () => listUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await createUser(f); toast.success("Utente creato"); setF({ email: "", password: "", name: "", role: "staff" }); load(); } catch (er) { toast.error("Errore", { description: er.response?.data?.detail || "" }); } };
  const del = async (id) => { await deleteUser(id); toast.success("Utente eliminato"); load(); };
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <form onSubmit={create} className="space-y-3 rounded-xl border border-white/10 bg-[#11141A] p-4">
        <h4 className="font-semibold text-white">Nuovo account staff/manager</h4>
        <input required data-testid="user-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <input required data-testid="user-password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password (min 8)" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <select data-testid="user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white"><option value="staff">Staff</option><option value="manager">Manager</option></select>
        <button data-testid="user-create-btn" className="w-full rounded-full bg-[#2563EB] px-4 py-2 font-semibold text-white">Crea account</button>
      </form>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid="user-row" className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#11141A] p-3 text-sm">
            <span className="text-white">{u.name}</span><span className="text-zinc-500">{u.email}</span>
            <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${u.role === "admin" ? "bg-rose-500/15 text-rose-400" : u.role === "manager" ? "bg-purple-500/15 text-purple-400" : "bg-blue-500/15 text-blue-400"}`}>{u.role}</span>
            {u.role !== "admin" && <button onClick={() => del(u.id)} className="rounded-full border border-white/10 px-2 py-1 text-xs text-zinc-400">Elimina</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function CRM() {
  const [leads, setLeads] = useState([]); const [meta, setMeta] = useState({ statuses: [], origins: [] });
  const load = () => listLeads({}).then(setLeads).catch(() => {});
  useEffect(() => { crmMeta().then(setMeta).catch(() => {}); load(); }, []);
  const change = async (id, status) => { await patchLead(id, { status }); toast.success("Stato lead aggiornato"); load(); };
  return (
    <div className="space-y-2">
      <p className="text-sm text-zinc-400">Lead da sito, AI, riparazioni, ordini. {leads.length} totali.</p>
      {leads.map((l) => (
        <div key={l.id} data-testid="lead-row" className="rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-zinc-400">{l.source}</span>
            <span className="font-semibold text-white">{[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}</span>
            <span className="text-zinc-500">{l.phone}</span>
            <select data-testid="lead-status" value={l.status} onChange={(e) => change(l.id, e.target.value)} className="ml-auto rounded-lg border border-white/10 bg-[#0B0E13] px-2 py-1 text-xs text-white">{meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            {(l.whatsapp || l.phone) && <a href={waLink(`Ciao ${l.first_name || ""}`)} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#25D366]/90 px-2 py-1 text-xs text-white">WA</a>}
          </div>
          {l.request && <p className="mt-1 text-zinc-400">{l.request}</p>}
        </div>
      ))}
    </div>
  );
}

function Ordini() {
  const [orders, setOrders] = useState([]);
  const load = () => adminOrders({}).then(setOrders).catch(() => {});
  useEffect(() => { load(); }, []);
  const change = async (id, status) => { await patchOrder(id, { status }); toast.success("Ordine aggiornato"); load(); };
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-400">Ordini con pagamento bonifico. Un ordine non è venduto finché non è PAGATO.</p>
      {orders.length === 0 && <p className="text-zinc-500">Nessun ordine.</p>}
      {orders.map((o) => (
        <div key={o.id} data-testid="order-row" className="rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[#2563EB]">{o.order_number}</span>
            <span className="font-semibold text-white">{o.customer_name}</span>
            <span className="text-zinc-500">{o.fulfillment} · {euro(o.total_cents)}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${o.payment_status === "PAID" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{o.payment_status === "PAID" ? "Pagato" : "Non pagato"}</span>
            <select data-testid="order-status-select" value={o.status} onChange={(e) => change(o.id, e.target.value)} className="ml-auto rounded-lg border border-white/10 bg-[#0B0E13] px-2 py-1 text-xs text-white">{ORDER_FLOW.map((s) => <option key={s} value={s}>{ORDER_STATUS_META[s]}</option>)}</select>
          </div>
          <div className="mt-1 text-xs text-zinc-500">{(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}</div>
          {o.fulfillment === "SPEDIZIONE" && <ShipmentEditor o={o} onDone={load} />}
        </div>
      ))}
    </div>
  );
}

function ShipmentEditor({ o, onDone }) {
  const s = o.shipment || {};
  const [carrier, setCarrier] = useState(s.carrier || "");
  const [tracking, setTracking] = useState(s.tracking_number || "");
  const [status, setStatus] = useState(s.status || "DA_PREPARARE");
  const save = async () => { await shipmentUpdate(o.id, { carrier, tracking_number: tracking, status }); toast.success("Spedizione aggiornata"); onDone(); };
  return (
    <div data-testid="shipment-editor" className="mt-3 rounded-lg border border-white/10 bg-[#0B0E13] p-3">
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span className="font-mono uppercase tracking-widest text-zinc-500">Spedizione</span>
        {s.high_value && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-400">Alto valore</span>}
        {(s.insurance_required || s.insurance_recommended) && <span className="rounded-full bg-purple-500/15 px-2 py-0.5 text-purple-300">Assicurata {s.insurance_required ? "obbligatoria" : "consigliata"}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input data-testid="shipment-carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Corriere" className="flex-1 rounded-lg border border-white/10 bg-[#11141A] px-2 py-1.5 text-xs text-white" />
        <input data-testid="shipment-tracking" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="N° tracking" className="flex-1 rounded-lg border border-white/10 bg-[#11141A] px-2 py-1.5 text-xs text-white" />
        <select data-testid="shipment-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-white/10 bg-[#11141A] px-2 py-1.5 text-xs text-white">{SHIPMENT_STATUSES.map((x) => <option key={x} value={x}>{SHIPMENT_META[x]}</option>)}</select>
        <button data-testid="shipment-save" onClick={save} className="rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white">Salva</button>
      </div>
    </div>
  );
}

function ShippingConfigCard() {
  const [c, setC] = useState(null);
  useEffect(() => { shippingConfig().then(setC).catch(() => {}); }, []);
  if (!c) return null;
  const save = async (e) => { e.preventDefault(); const r = await putShippingConfig({ ...c, high_value_threshold_cents: parseInt(c.high_value_threshold_cents), default_cost_cents: parseInt(c.default_cost_cents || 0) }); setC(r); toast.success("Spedizioni salvate"); };
  return (
    <form onSubmit={save} className="space-y-3 rounded-xl border border-white/10 bg-[#11141A] p-4">
      <h4 className="font-semibold text-white">Spedizioni</h4>
      <label className="block text-sm text-zinc-400">Soglia alto valore (€)
        <input data-testid="ship-threshold" type="number" value={(c.high_value_threshold_cents || 0) / 100} onChange={(e) => setC({ ...c, high_value_threshold_cents: Math.round(parseFloat(e.target.value) * 100) })} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" /></label>
      <label className="block text-sm text-zinc-400">Assicurazione alto valore
        <select data-testid="ship-policy" value={c.insurance_policy} onChange={(e) => setC({ ...c, insurance_policy: e.target.value })} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white"><option value="RACCOMANDATA">Raccomandata</option><option value="OBBLIGATORIA">Obbligatoria</option><option value="NESSUNA">Nessuna</option></select></label>
      <input value={c.default_carrier || ""} onChange={(e) => setC({ ...c, default_carrier: e.target.value })} placeholder="Corriere predefinito" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
      <button className="w-full rounded-full bg-[#2563EB] px-4 py-2 font-semibold text-white">Salva spedizioni</button>
    </form>
  );
}

function ProviderConfigCard() {
  const [c, setC] = useState(null);
  useEffect(() => { ppConfig().then(setC).catch(() => {}); }, []);
  if (!c) return null;
  const upd = async (patch) => { const r = await putPpConfig(patch); setC(r); toast.success("Provider prezzi aggiornato"); };
  return (
    <div className="space-y-3 rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
      <div className="flex items-center justify-between"><h4 className="font-semibold text-white">Provider prezzi ricambi (eBay)</h4>
        <button data-testid="pp-toggle" onClick={() => upd({ enabled: !c.enabled })} className={`rounded-full px-3 py-1 text-xs font-semibold ${c.enabled ? "bg-emerald-500/90 text-white" : "border border-white/15 text-zinc-300"}`}>{c.enabled ? "Attivo" : "Disattivo"}</button></div>
      <label className="block text-zinc-400">Modalità
        <select data-testid="pp-mode" value={c.mode} onChange={(e) => upd({ mode: e.target.value })} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white"><option value="AI_SEARCH">Ricerca assistita (riferimento di mercato)</option><option value="OFFICIAL_API">API ufficiale eBay (da collegare)</option><option value="DISABLED">Disattivato</option></select></label>
      <p className="text-xs text-zinc-500">{c.note}</p>
    </div>
  );
}

function Impostazioni() {
  const [bank, setBank] = useState(null); const [wa, setWa] = useState(null);
  useEffect(() => { bankConfig().then(setBank).catch(() => {}); waConfig().then(setWa).catch(() => {}); }, []);
  const save = async (e) => { e.preventDefault(); const c = await putBankConfig(bank); setBank(c); toast.success("Dati bonifico salvati"); };
  if (!bank) return <p className="text-zinc-500">Caricamento…</p>;
  const set = (k) => (e) => setBank({ ...bank, [k]: e.target.value });
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-3 rounded-xl border border-white/10 bg-[#11141A] p-4">
        <div className="flex items-center justify-between"><h4 className="font-semibold text-white">Bonifico bancario</h4>
          <button type="button" data-testid="bank-enabled-toggle" onClick={() => setBank({ ...bank, enabled: !bank.enabled })} className={`rounded-full px-3 py-1 text-xs font-semibold ${bank.enabled ? "bg-emerald-500/90 text-white" : "border border-white/15 text-zinc-300"}`}>{bank.enabled ? "Attivo" : "Disattivo"}</button></div>
        <input data-testid="bank-holder" value={bank.holder || ""} onChange={set("holder")} placeholder="Intestatario" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <input data-testid="bank-iban" value={bank.iban || ""} onChange={set("iban")} placeholder="IBAN" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white font-mono" />
        <input value={bank.bank || ""} onChange={set("bank")} placeholder="Banca" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <input value={bank.bic || ""} onChange={set("bic")} placeholder="BIC/SWIFT" className="w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" />
        <button data-testid="bank-save-btn" className="w-full rounded-full bg-[#2563EB] px-4 py-2 font-semibold text-white">Salva</button>
      </form>
      <div className="space-y-3 rounded-xl border border-white/10 bg-[#11141A] p-4 text-sm">
        <h4 className="font-semibold text-white">WhatsApp Business</h4>
        <p className="text-zinc-400">Stato: <span className="text-amber-400">{wa?.provider || "NON_CONFIGURATO"}</span></p>
        <p className="text-xs text-zinc-500">{wa?.note}</p>
        <p className="text-xs text-zinc-500">Invio automatico: {wa?.auto_send ? "Sì" : "No (disabilitato)"} · Consenso richiesto: {wa?.consent_required ? "Sì" : "No"}</p>
      </div>
      <ShippingConfigCard />
      <ProviderConfigCard />
    </div>
  );
}



function Magazzino() {
  const [rows, setRows] = useState([]);
  const load = () => inventoryList("").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const move = async (variant_id, type) => { await inventoryMovement({ type, variant_id, qty: 1 }); toast.success(`Movimento ${type}`); load(); };
  return (
    <div className="space-y-2">
      {rows.map((r) => <div key={r.variant_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-[#11141A] p-3 text-sm">
        <span className="text-white">{r.product_name}</span><span className="text-zinc-500">{r.storage} {r.color}</span>
        <span className="ml-auto text-zinc-400">Q: <b className="text-white">{r.quantity}</b> · Ris: {r.reserved_qty} · {r.status}</span>
        <button onClick={() => move(r.variant_id, "CARICO")} className="rounded-full border border-white/10 px-2 py-1 text-xs">+ Carico</button>
        <button onClick={() => move(r.variant_id, "VENDITA")} className="rounded-full border border-white/10 px-2 py-1 text-xs">− Vendita</button>
        <button onClick={() => move(r.variant_id, "PRENOTAZIONE")} className="rounded-full border border-white/10 px-2 py-1 text-xs">Prenota</button>
      </div>)}
    </div>
  );
}

function AiAdmin() {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [convs, setConvs] = useState([]);
  const load = () => {
    aiGetConfig().then(setCfg).catch(() => {});
    aiUsage().then(setUsage).catch(() => {});
    aiConversations().then(setConvs).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const c = await aiPutConfig(patch);
    setCfg(c); toast.success("Configurazione AI salvata");
  };
  if (!cfg) return <p className="text-zinc-500">Caricamento…</p>;
  const budgetPct = usage ? Math.min(100, Math.round((usage.budget_usato_oggi_cents / (usage.budget_giornaliero_cents || 1)) * 100)) : 0;
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPI label="Conversazioni" value={usage?.conversazioni ?? "—"} />
        <KPI label="Chiamate AI" value={usage?.chiamate ?? "—"} />
        <KPI label="Token totali" value={usage?.token_totali ?? "—"} />
        <KPI label="Costo stimato" value={euro(usage?.costo_stimato_cents || 0)} />
      </section>

      <section className="rounded-xl border border-white/10 bg-[#11141A] p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Budget giornaliero</h3>
          <span className="text-sm text-zinc-300">{euro(usage?.budget_usato_oggi_cents || 0)} / {euro(usage?.budget_giornaliero_cents || 0)}</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10"><div className={`h-full ${budgetPct > 90 ? "bg-rose-500" : "bg-[#2563EB]"}`} style={{ width: `${budgetPct}%` }} /></div>
      </section>

      <section className="rounded-xl border border-white/10 bg-[#11141A] p-4">
        <div className="flex items-center justify-between">
          <div><h3 className="font-semibold text-white">Assistente AI</h3><p className="text-xs text-zinc-500">Attiva o disattiva il widget di chat sullo storefront</p></div>
          <button data-testid="ai-toggle-btn" onClick={() => save({ enabled: !cfg.enabled })} className={`rounded-full px-4 py-2 text-sm font-semibold ${cfg.enabled ? "bg-emerald-500/90 text-white" : "border border-white/15 text-zinc-300"}`}>{cfg.enabled ? "Attivo" : "Disattivato"}</button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[["daily_budget_cents", "Budget giornaliero (€)", true], ["max_messages_per_session", "Max messaggi / sessione", false],
            ["max_messages_per_ip_hour", "Max messaggi / IP / ora", false], ["cost_per_million_tokens_cents", "Costo stima (cent / 1M token)", false]].map(([key, label, isEuro]) => (
            <label key={key} className="text-sm text-zinc-400">{label}
              <input data-testid={`ai-cfg-${key}`} type="number" defaultValue={isEuro ? (cfg[key] / 100) : cfg[key]}
                onBlur={(e) => { const v = isEuro ? Math.round(parseFloat(e.target.value) * 100) : parseInt(e.target.value); if (!isNaN(v)) save({ [key]: v }); }}
                className="mt-1 w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-white" /></label>
          ))}
        </div>
        <p className="mt-3 text-xs text-zinc-500">Modello: <span className="text-zinc-300">{cfg.model}</span> · Provider: {cfg.provider}. L'AI legge solo dal database/Pricing Engine e non può modificare prezzi.</p>
      </section>

      <section>
        <h3 className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Conversazioni recenti</h3>
        <div className="space-y-2">
          {convs.length === 0 && <p className="text-zinc-500">Nessuna conversazione.</p>}
          {convs.map((c) => (
            <div key={c.id} data-testid="ai-conv-row" className="rounded-xl border border-white/10 bg-[#11141A] p-3 text-sm">
              <div className="flex items-center gap-2"><span className="font-mono text-xs text-[#2563EB]">{c.session_id.slice(0, 8)}</span>
                <span className="text-zinc-400">{c.message_count} messaggi</span>
                <span className="ml-auto text-xs text-zinc-600">{c.updated_at?.slice(0, 16).replace("T", " ")}</span></div>
              {c.ultimo_messaggio && <p className="mt-1 truncate text-zinc-300">“{c.ultimo_messaggio}”</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}


function Catalogo() {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminCatalog({ source: "erre_game" }).then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const fetchImgs = async () => { setBusy(true); try { const r = await fetchCatalogImages(); toast.success(r.note); } catch { toast.error("Errore avvio ricerca immagini"); } finally { setBusy(false); } };
  const filtered = cat ? rows.filter((r) => r.category_slug === cat) : rows;
  const withImg = rows.filter((r) => r.image).length;
  return (
    <section className="space-y-4" data-testid="admin-catalog">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Catalogo Erre Game · {rows.length} prodotti</h3>
        <span className="text-xs text-zinc-500">Foto ufficiali: {withImg}/{rows.length}</span>
        <button data-testid="fetch-images-btn" disabled={busy} onClick={fetchImgs} className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-zinc-200 disabled:opacity-50"><Image className="mr-1 inline h-3 w-3" /> Cerca immagini ufficiali</button>
        <button onClick={load} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300">Aggiorna</button>
      </div>
      <div className="flex gap-2">
        {[["", "Tutti"], ["smartphone", "Smartphone"], ["tablet", "Tablet"]].map(([v, l]) => (
          <button key={v} data-testid={`catfilter-${v || "all"}`} onClick={() => setCat(v)} className={`rounded-full border px-3 py-1 text-xs ${cat === v ? "border-[#2563EB] bg-[#2563EB]/20 text-white" : "border-white/10 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/5 text-[11px] uppercase text-zinc-500">
            <tr><th className="p-2">Prodotto</th><th className="p-2">Brand</th><th className="p-2">Cat.</th><th className="p-2 text-right">Costo</th><th className="p-2 text-right">Vendita</th><th className="p-2 text-right">Margine</th><th className="p-2 text-center">Foto</th></tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid="catalog-row" className="border-t border-white/5">
                <td className="max-w-xs truncate p-2 text-white">{p.name}</td>
                <td className="p-2 text-zinc-400">{p.brand}</td>
                <td className="p-2 text-zinc-400">{p.category}</td>
                <td className="p-2 text-right text-zinc-400">{euro(p.cost_cents)}</td>
                <td className="p-2 text-right font-semibold text-white">{euro(p.price_cents)}</td>
                <td className="p-2 text-right text-emerald-400">{p.margin_cents != null ? euro(p.margin_cents) : "—"}{p.margin_pct != null ? ` · ${p.margin_pct}%` : ""}</td>
                <td className="p-2 text-center">{p.image ? <span className="text-emerald-400">✓</span> : <span className="text-zinc-600">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessun prodotto. Pubblica il listino telefoni dalla tab Listini.</p>}
    </section>
  );
}


export default function ControlCenter() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("dash");
  const [tab, setTab] = useState("dash");
  const activeSection = SECTIONS.find((s) => s.id === section) || SECTIONS[0];
  return (
    <div className="min-h-screen bg-[#090B0E] text-white">
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/10 bg-[#0B0E13] px-4 py-3">
        <span className="font-display text-lg font-extrabold">IPHIX <span className="text-[#2563EB]">Control Center</span></span>
        <span className="ml-auto text-sm text-zinc-400">{user?.email} · {user?.role}</span>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-sm text-zinc-300"><LogOut className="h-4 w-4" /> Esci</button>
      </header>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex flex-wrap gap-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button key={s.id} data-testid={`nav-${s.id}`}
                onClick={() => { setSection(s.id); setTab(s.subs[0][0]); }}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${section === s.id ? "bg-[#2563EB] text-white" : "border border-white/10 text-zinc-300 hover:border-[#2563EB]/50"}`}>
                <Icon className="h-4 w-4" /> {s.label}
              </button>);
          })}
        </div>
        {activeSection.subs.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2 border-t border-white/5 pt-3">
            {activeSection.subs.map(([id, label]) => (
              <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${tab === id ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}>
                {label}
              </button>))}
          </div>
        )}
        <div className="mb-6" />
        {tab === "dash" && <Dashboard />}
        {tab === "analytics" && <Analytics />}
        {tab === "agenda" && <Agenda />}
        {tab === "valutazioni" && <Valutazioni />}
        {tab === "usato" && <UsatoAdmin />}
        {tab === "pricing" && <Pricing />}
        {tab === "import" && <ImportPanel />}
        {tab === "listini-man" && <Listini />}
        {tab === "fornitori" && <Suppliers />}
        {tab === "nwg" && <NWGLists />}
        {tab === "volantini" && <Volantini />}
        {tab === "immagini" && <Immagini />}
        {tab === "magazzino" && <Magazzino />}
        {tab === "ordini" && <Ordini />}
        {tab === "crm" && <CRM />}
        {tab === "catalogo" && <Catalogo />}
        {tab === "social" && <Promozioni />}
        {tab === "storico" && <PriceHistory />}
        {tab === "copilot" && <Copilot />}
        {tab === "utenti" && <Utenti />}
        {tab === "ai" && <AiAdmin />}
        {tab === "settings" && <Impostazioni />}
      </div>
    </div>
  );
}
